import { MissingEntityError, trackError } from '#api/errors.ts';
import { useSuspenseMaxRoutesQuery } from '#api/routes.queries.ts';
import { useSuspenseMaxTagsQuery } from '#api/tags.queries.ts';
import type { TagName } from '#dn-types/host.ts';

export class MissingRouteError extends MissingEntityError {
  constructor(routeId: string) {
    super(routeId);
    this.name = 'MissingRouteError';
  }
}

type Props = {
  tagNames: TagName[];
};

/**
 * Calculates the subscribed routes for a host based on its tag names.
 * Throws a custom error if a route name cannot be found for some reason.
 */
export function useHostRoutes({ tagNames }: Props) {
  const allRoutes = useSuspenseMaxRoutesQuery().data.data;
  const allTags = useSuspenseMaxTagsQuery().data.data;
  const hostTags = allTags.filter((t) => tagNames.includes(t.name));

  // Flat map the tag's route subscriptions into route and source pairs
  return (
    hostTags
      .flatMap((tag) =>
        tag.routeSubscriptions.map((routeId) => {
          const route = allRoutes.find((r) => r.id === routeId);

          if (!route) {
            const err = new MissingRouteError(routeId);
            trackError(err);
            return err;
          }

          return {
            source: tag.name,
            route,
          };
        })
      )
      // Sort routes by route name, the list was already sorted by priority.
      .sort((a, b) => {
        if (a instanceof MissingRouteError) return 1;
        if (b instanceof MissingRouteError) return -1;

        return a.route.name.localeCompare(b.route.name);
      })
  );
}
